import type {SoundFrame} from './score';
import {FREELANCE_SCORE} from '../narrative/freelanceScore';
// +6.02 dB on the existing musical envelopes. The accepted finale recording
// is a separate score cue and never passes through this calibration.
const AMBIENCE_GAIN=2;
const smooth=(v:number)=>{const t=Math.max(0,Math.min(1,v));return t*t*(3-2*t);};

/** Exact encoded sources recovered from the author's final browser studio.
 * See assets-source/audio/trailer-mix.json, not the earlier HQ alternates. */
export const TRAILER_SOURCES = ['logo_ambient_89','a01_dead_v1','a03_open_v1','syn_glass_ping','logo_IMPACT_full','logo_ORIGINAL_18s'] as const;
export const FOLEY_SOURCES=['foley_mouse','foley_key1','foley_key2','foley_key3','physical_switch','foley_camera'] as const;
export const MOTION_SOURCES=['L_off_2'] as const;
export const AUDIO_SOURCES=[...TRAILER_SOURCES,...FOLEY_SOURCES,...MOTION_SOURCES] as const;
export type TrailerSource=typeof AUDIO_SOURCES[number];

export function ambienceLevel(f:SoundFrame):number {
 if(f.hidden||f.paused||f.reduced||f.chapter==='uses-video')return 0;
 const levels:Record<string,number>={film:.17,intro:.14,workflow:.12,'uses-game':.12,'uses-site':.12,'uses-freelance':.13,'uses-more':.085,share:.085,audience:.12,closer:.08,pricing:.10,install:.08,footer:.075};
 if(f.phase==='hero'){
  const opening=f.opening;
  if(!opening)return .075*AMBIENCE_GAIN;
  const fade=Math.max(0,Math.min(1,(opening.elapsed-opening.settledAt-800)/3200));
  return .075*AMBIENCE_GAIN*fade*fade*(3-2*fade);
 }
 let level=levels[f.chapter]??.6;
 if(f.chapter==='film'&&f.p<.64)level=.09;
 if(f.chapter==='film'&&f.trailerTime!=null){
  const incoming=1-smooth(f.trailerTime/4.5),returning=smooth((f.trailerTime-9.2)/4.8);
  level*=Math.max(.24,incoming,returning);
 }
 // The music leaves with the picture, before the video owns the soundtrack.
 if(f.chapter==='uses-site'&&f.phase==='transition')level*=1-smooth(f.transition/3000);
 // Let the bridge's ascending construction notes speak in the returning silence.
 if(f.chapter==='uses-freelance')level*=.12*smooth(f.elapsed/6500)+.88*smooth((f.elapsed-FREELANCE_SCORE.materials)/6500);
 // The finale clock holds at 8 seconds; restore the bed before that hold,
 // so the source recording can decay into it without leaving permanent silence.
 if(f.chapter==='footer')level*=Math.max(1-smooth(f.elapsed/2400),.7*smooth((f.elapsed-5200)/2800));
 // A reading pause breathes but does not kill the world. Film pauses are silent.
 // Reading and chapter travel retain the same bed; only explicit pause mutes.
 // Broad phrases breathe with the score; no rapid tremolo or arbitrary panning.
 return AMBIENCE_GAIN*level*(.72+.18*Math.sin(f.clock/11500)+.10*Math.sin(f.clock/7300+.7));
}
